import { 
  LayoutDashboard, 
  Briefcase, 
  Folder, 
  FileText, 
  Route, 
  MapPin, 
  CalendarDays, 
  UserPlus,
  CreditCard,
  BarChart3,
  MessageSquarePlus,
  Sparkles,
  LucideIcon,
  Search,
  DollarSign,
  History,
  ShieldCheck
} from 'lucide-react';

export interface OnboardingStep {
  id: string;
  navHref: string;
  icon: LucideIcon;
  title: string;
  description: string;
  tips: string[];
  ctaText?: string;
  ctaHref?: string;
}

export const clientOnboardingSteps: OnboardingStep[] = [
  {
    id: 'dashboard',
    navHref: '/dashboard',
    icon: LayoutDashboard,
    title: 'Welcome to Your Dashboard',
    description: 'Your central hub for monitoring all procurement activities. View key metrics, track job progress, and access recent updates.',
    tips: [
      'Check your dashboard daily for important updates',
      'Use quick stats to monitor job progress',
      'Review upcoming milestones to stay on schedule'
    ]
  },
  {
    id: 'jobs',
    navHref: '/jobs',
    icon: Briefcase,
    title: 'Create & Manage RFQs',
    description: 'Jobs are Request for Quotes (RFQs) where you define requirements, invite vendors to bid, compare proposals, and award contracts.',
    tips: [
      'Start with specifications or templates to save time',
      'Add multiple line items for complex orders',
      'Set clear deadlines to get faster vendor responses',
      'Review all bids carefully before awarding'
    ],
    ctaText: 'Create Your First Job',
    ctaHref: '/jobs/new'
  },
  {
    id: 'projects',
    navHref: '/projects',
    icon: Folder,
    title: 'Organize with Projects',
    description: 'Group related jobs together into projects for better organization and tracking. Perfect for multi-phase initiatives or campaigns.',
    tips: [
      'Use projects for related procurement needs',
      'Track overall project budgets and timelines',
      'Easily view all jobs within a project'
    ]
  },
  {
    id: 'specifications',
    navHref: '/specifications',
    icon: FileText,
    title: 'Build Reusable Specifications',
    description: 'Create detailed product specifications that can be reused across multiple jobs. Define attributes, requirements, and standards.',
    tips: [
      'Build a library of common specs to speed up job creation',
      'Include detailed requirements to reduce vendor questions',
      'Update specifications as standards evolve'
    ]
  },
  {
    id: 'milestones',
    navHref: '/milestones',
    icon: Route,
    title: 'Create Reusable Milestone Templates',
    description: 'Create reusable sets of tasks and deliverables for your jobs. Standardize the steps across similar jobs using milestone templates to ensure nothing gets missed.',
    tips: [
      'Build templates for common job types you run regularly',
      'Define clear tasks, deliverables, and owners for each step',
      'Apply templates to new jobs with one click',
      'Track all milestone deadlines across jobs in the calendar'
    ]
  },
  {
    id: 'addresses',
    navHref: '/addresses',
    icon: MapPin,
    title: 'Save Shipping Addresses',
    description: 'Create a library of frequently used addresses for proofs, samples, and final delivery. Quickly select saved locations when creating jobs.',
    tips: [
      'Add all your facility and delivery locations upfront',
      'Include contact information for each location',
      'Use saved addresses for proofs, samples, and delivery locations'
    ]
  },
  {
    id: 'calendar',
    navHref: '/calendar',
    icon: CalendarDays,
    title: 'Track Milestone Deadlines',
    description: 'View all upcoming milestone deadlines across all your active jobs in a single calendar view.',
    tips: [
      'Check weekly to stay ahead of deadlines',
      'Click any milestone to view details or take action',
      'Use filters to focus on specific jobs or date ranges'
    ]
  },
  {
    id: 'reports',
    navHref: '/reports',
    icon: BarChart3,
    title: 'Analyze Your Procurement Data',
    description: 'Access powerful reports and analytics to make data-driven procurement decisions. Track spending, vendor performance, cycle times, and diversity metrics.',
    tips: [
      'Review spend reports to monitor budget utilization',
      'Track vendor performance and engagement metrics',
      'Analyze cycle times to identify process bottlenecks',
      'Monitor diversity spend and track supplier diversity goals'
    ]
  },
  {
    id: 'invite-vendors',
    navHref: '/vendors/invite',
    icon: UserPlus,
    title: 'Build Your Vendor Network',
    description: 'Invite your existing vendors to join the platform and participate in your RFQs. Build your vendor network.',
    tips: [
      'Invite vendors you already work with',
      'Track invitation status and vendor activity',
      'Vendors with complete profiles can submit more detailed bids'
    ]
  },
  {
    id: 'feedback',
    navHref: '/feedback',
    icon: MessageSquarePlus,
    title: 'Share Your Feedback',
    description: 'Help us improve the platform by reporting bugs, suggesting new features, or sharing general feedback. Your input shapes our roadmap.',
    tips: [
      'Report bugs with screenshots for faster resolution',
      'Suggest features you would find valuable',
      'Track the status of your submitted feedback',
      'Attach files to provide more context'
    ],
    ctaText: 'Submit Feedback',
    ctaHref: '/feedback'
  },
  {
    id: 'payment-setup',
    navHref: 'user-menu',
    icon: CreditCard,
    title: 'Secure Payment Processing',
    description: 'Set up your payment method in Account Settings to enable seamless transactions.\n\nWe use Stripe as our secure payment processor. Your payment information is never stored on our platform, ensuring maximum security for all transactions.',
    tips: [
      'We accept Credit Card and ACH payments',
      'Credit Card payments have a 2.9% processing fee; ACH transfers have no fee',
      'Set up your payment method in Account Settings',
      'You can also add payment info when awarding a job',
      'All payments are PCI compliant and fully encrypted'
    ],
    ctaText: 'Setup Payment Method',
    ctaHref: '/settings/account'
  },
  {
    id: 'welcome-complete',
    navHref: '/dashboard',
    icon: Sparkles,
    title: "You're Ready to Go!",
    description: "Thank you for choosing Radius! We're a highly responsive team dedicated to making your procurement experience successful. We're here to help every step of the way.",
    tips: [
      'Need help right away? Use the live chat in the lower right corner of your screen',
      'Our team typically responds to support requests within hours',
      'Use the Feedback page to share ideas or report issues directly',
      "We're constantly improving based on your feedback"
    ],
    ctaText: 'Go to Dashboard',
    ctaHref: '/dashboard'
  }
];

export const vendorOnboardingSteps: OnboardingStep[] = [
  {
    id: 'vendor-dashboard',
    navHref: '/dashboard',
    icon: LayoutDashboard,
    title: 'Welcome to Your Vendor Dashboard',
    description: 'Your central hub for tracking opportunities, managing bids, and monitoring your active jobs.',
    tips: [
      'Check your dashboard daily for new RFQ opportunities',
      'Monitor your bid win rate and performance metrics',
      'Stay on top of upcoming deadlines and milestones'
    ]
  },
  {
    id: 'browse-rfqs',
    navHref: '/rfqs',
    icon: Search,
    title: 'Browse Available RFQs',
    description: 'Discover new business opportunities by browsing open Requests for Quotes from clients looking for your services.',
    tips: [
      'All public RFQs will appear here. Private RFQs that you have been invited to are visually distinct.',
      'When RFQs are created the client can include criteria, we use that criteria to assign you a match score for every RFQ. Click the score for details.',
      'Note: You must be verified to view full RFQ details'
    ]
  },
  {
    id: 'my-bids',
    navHref: '/bids',
    icon: DollarSign,
    title: 'My Bids',
    description: 'After you bid on an RFQ it will appear in your My Bids page.',
    tips: [
      'Monitor bid status and client responses',
      'Bids can be withdrawn and resubmitted'
    ]
  },
  {
    id: 'vendor-jobs',
    navHref: '/jobs',
    icon: Briefcase,
    title: 'Your Active Jobs',
    description: 'Once you win a bid, the job moves here. Manage deliverables, upload proofs, and track milestones.',
    tips: [
      'You will be issued a purchase order after the Milestone timeline has been approved by both parties',
      'The job interface includes management for timelines, messaging, files, shipments, and payments.'
    ]
  },
  {
    id: 'vendor-calendar',
    navHref: '/calendar',
    icon: CalendarDays,
    title: 'Track Your Deadlines',
    description: 'View all upcoming job milestones in a unified calendar or list view.',
    tips: [
      'All Milestones include in-platform and email reminders leading up to the due date.',
      'Click any event to view details or take action',
      'Plan your workload across multiple jobs'
    ]
  },
  {
    id: 'bid-history',
    navHref: '/bid-history',
    icon: History,
    title: 'Review Your Bid History',
    description: 'Access your complete bidding history to analyze patterns, review past proposals, and improve future bids.',
    tips: [
      'Learn from successful bids to improve win rates',
      'Reference past pricing for similar projects',
      'Track your bidding activity over time'
    ]
  },
  {
    id: 'vendor-feedback',
    navHref: '/feedback',
    icon: MessageSquarePlus,
    title: 'Share Your Feedback',
    description: 'Help us improve the platform by reporting bugs, suggesting features, or sharing your experience.',
    tips: [
      'Report issues with screenshots for faster resolution',
      'Suggest features that would help your business',
      'Track the status of your submitted feedback'
    ]
  },
  {
    id: 'vendor-profile-verification',
    navHref: 'user-menu',
    icon: ShieldCheck,
    title: 'Complete Your Profile & Get Verified',
    description: "Complete your vendor profile and request verification to unlock full platform access.\n\nImportant: You cannot view detailed RFQ information until your profile is verified by our team.",
    tips: [
      'Add your business description, capabilities, and service areas',
      'Upload required documentation for verification',
      'Verification typically takes 1-2 business days',
      'Your profile is visible to clients during bid review and private RFQ invitation. A complete and high-quality profile will help you win.'
    ],
    ctaText: 'Complete Your Profile',
    ctaHref: '/profile/vendor'
  },
  {
    id: 'vendor-stripe-setup',
    navHref: 'user-menu',
    icon: CreditCard,
    title: 'Set Up Payment Processing',
    description: "Visit the Account Settings page and create a Stripe Connect account to receive payments for completed jobs.\n\nImportant: You cannot submit bids until your payment account is fully configured.",
    tips: [
      'We use Stripe for secure, reliable payments',
      'Payments can be automatically deposited to your bank account',
      'Set up takes just a few minutes',
      'Your financial information is never stored on our servers'
    ],
    ctaText: 'Setup Payment Account',
    ctaHref: '/settings/account'
  },
  {
    id: 'vendor-welcome-complete',
    navHref: '/dashboard',
    icon: Sparkles,
    title: "You're Ready to Go!",
    description: "Thank you for joining Radius! We're a highly responsive team dedicated to helping you grow your business and win more jobs. We're here to support you every step of the way.",
    tips: [
      'Need help? Use the live chat in the lower right corner',
      'Our team typically responds to support requests within hours',
      'Complete your profile and verification to start bidding',
      'Use the Feedback page to share ideas or report issues'
    ],
    ctaText: 'Go to Dashboard',
    ctaHref: '/dashboard'
  }
];
